import React, { useEffect, useState } from "react";
import { Modal, Table } from "../../../../components/ui";
import { useParams } from "react-router-dom";
import {
  GetEventPhotosService,
  ToggleImageVisibilityService,
} from "../../../../apis/events";
import { Badge, Button, Empty, Image, Pagination, Spin, Switch } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { setTotalPictures } from "../../../../redux/actions/photosActions";
import { scrollToTop } from "../../../../utils/commonFunctions";

export default function EventPhotos() {
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [view, SetView] = useState("List");
  const [loading, setLoading] = useState(false);
  const [visibilityLoading, setVisibilityLoading] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showInappropriate, setShowInappropriate] = useState(false);
  const [page, setPage] = useState(1);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [issuesList, setIssuesList] = useState({
    loading: false,
    show: false,
    data: [],
  });

  const totalPictures = useSelector((state) => state.photos.totalPictures);
  const dispatch = useDispatch();
  const issueColumns = [
    {
      title: "Issue Description",
      dataIndex: "note",
      key: "note",
    },
    {
      title: "Reported By",
      key: "reported_by",
      render: (text, record) => <span>{record.user_detail.mobile_number}</span>,
    },
  ];

  const getEventPhotos = async (page = 1, page_size = 20) => {
    setLoading(true);
    try {
      const body = {
        event_id: id,
        is_inappropriate: showInappropriate ? 1 : 0,
        page,
        page_size,
      };
      const res = await GetEventPhotosService(body);
      if (res.status === 200 && res.data.success) {
        const _d = res.data.photo_list;
        const ls = _d.map((item) => ({ ...item, key: item.id }));
        if (page === 1) {
          dispatch(setTotalPictures(res.data.total_photos_count));
        }
        setData(ls);
      } else {
        setData([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEventPhotos(page, 20);
  }, [showInappropriate, page]); // eslint-disable-line

  const handleChangeGalleryVisibility = async (checked, id, type) => {
    setVisibilityLoading(id);
    try {
      const body = {
        image_ids: type === "single" ? [id] : selectedRowKeys,
        status: checked ? 1 : 0,
      };

      const response = await ToggleImageVisibilityService(body);
      if (response.status === 200 && response.data.success) {
        getEventPhotos();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setVisibilityLoading(null);
    }
  };

  const handleGetPhotoIssues = async (data) => {
    setIssuesList({
      show: true,
      data: data.image_report,
    });
  };

  const columns = [
    {
      title: "Photo",
      dataIndex: "image_url",
      key: "name",
      render: (text, record) => (
        <Badge
          count={
            record.image_report.length ? (
              <WarningOutlined style={{ color: "red" }} />
            ) : (
              0
            )
          }
        >
          <Image
            width={40}
            height={40}
            src={text}
            fallback="https://via.placeholder.com/150"
            preview={{
              visible: false,
              onVisibleChange: (visible) => {
                if (visible) {
                  const idx = data.findIndex((d) => d.id === record.id);
                  setPreviewIndex(idx);
                  setPreviewVisible(true);
                }
              },
            }}
          />
        </Badge>
      ),
    },
    {
      title: "Uploaded By",
      dataIndex: "uploaded_by",
      key: "uploaded_by",
      render: (uploadedBy) => {
        if (!uploadedBy) return "-";
        if (uploadedBy.is_admin) return <span style={{ fontWeight: 500 }}>Admin</span>;
        const parts = [uploadedBy.mobile, uploadedBy.email].filter(Boolean);
        return parts.length ? (
          <div style={{ lineHeight: 1.4 }}>
            {uploadedBy.mobile && <div>{uploadedBy.mobile}</div>}
            {uploadedBy.email && <div style={{ color: "#595C5C", fontSize: 12 }}>{uploadedBy.email}</div>}
          </div>
        ) : "-";
      },
    },
    {
      title: "Show in Gallery",
      dataIndex: "is_in_gallery",
      key: "is_in_gallery",
      render: (text, record) => (
        <>
          <Switch
            size="small"
            loading={visibilityLoading && visibilityLoading === record.id}
            checked={text === 1}
            onChange={(checked) => {
              handleChangeGalleryVisibility(checked, record.id, "single");
            }}
            disabled={visibilityLoading && visibilityLoading !== null}
          />
        </>
      ),
      width: 140, // Set a fixed width for the "Show in Gallery" column
    },
    {
      title: "Inappropriate Reason",
      dataIndex: "error",
      key: "error",
      width: 220, // Set a fixed width for the "Inappropriate Reason" column
      render: (text, record) => {
        let errorArr = text;
        if (typeof text === "string") {
          try {
            errorArr = JSON.parse(text);
          } catch (e) {
            errorArr = [];
          }
        }
        return (
          <span>
            {errorArr?.length > 0
              ? errorArr.map((item, idx) => (
                  <span
                    key={idx}
                    className="mb-1 d-inline-block"
                    style={{
                      background: "rgba(228, 79, 86, 1)",
                      color: "#fff",
                      borderRadius: "16px",
                      padding: "2px 10px",
                      marginRight: "6px",
                      fontSize: "12px",
                    }}
                  >
                    {item?.name}
                  </span>
                ))
              : null}
          </span>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 100, // Set a fixed width for the "Action" column
      render: (text, record) => (
        <>
          {record.image_report.length > 0 && (
            <Button
              icon={<WarningOutlined style={{ color: "red" }} />}
              title="View Issues"
              onClick={() => {
                handleGetPhotoIssues(record);
              }}
            />
          )}
        </>
      ),
    },
  ];

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3"></div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="page-title m-0">Event Gallery</h3>
          {hasSelected ? (
            <>
              <p>
                Selected {selectedRowKeys.length} items of {data.length} items |{" "}
                {selectedRowKeys.length === data.length ? (
                  <span
                    className="text-primary cursor-pointer"
                    onClick={() => {
                      setSelectedRowKeys([]);
                    }}
                  >
                    Clear Selection
                  </span>
                ) : (
                  <span
                    className="text-primary cursor-pointer"
                    onClick={() => {
                      setSelectedRowKeys(data.map((item) => item.id));
                    }}
                  >
                    Select all {data.length} items
                  </span>
                )}
              </p>
            </>
          ) : null}
        </div>
        <div>
          {hasSelected ? (
            <>
              <Button
                type="primary"
                className="me-2"
                onClick={() => {
                  handleChangeGalleryVisibility(
                    false,
                    selectedRowKeys,
                    "multiple",
                  );
                }}
                disabled={!hasSelected}
                loading={loading}
              >
                Hide from galley
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  handleChangeGalleryVisibility(
                    true,
                    selectedRowKeys,
                    "multiple",
                  );
                }}
                disabled={!hasSelected}
                loading={loading}
              >
                Show in gallery
              </Button>
            </>
          ) : (
            <div className="d-flex align-items-center">
              <Switch
                checkedChildren="Hide Inappropriate"
                unCheckedChildren="Show Inappropriate"
                onChange={(checked) => {
                  setShowInappropriate(checked);
                  setPage(1);
                }}
                style={{ marginRight: 16 }}
              />
              <Button
                onClick={() => {
                  SetView("List");
                }}
                title="List View"
                shape="square"
                icon={<i className="fa-solid fa-list" />}
                style={{ marginRight: 10 }}
              />
              <Button
                onClick={() => {
                  SetView("Grid");
                }}
                title="Grid View"
                shape="square"
                icon={<i className="fa-solid fa-grip" />}
              />
            </div>
          )}
        </div>
      </div>
      {view === "List" && (
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowSelection={rowSelection}
          pagination={false}
        />
      )}
      {view === "Grid" && (
        <Spin spinning={loading}>
          <div className="row">
            {data?.length ? (
              <Image.PreviewGroup>
                {data.map((item, index) => (
                  <div className="col-lg-2 col-md-3 col-xs-3 p-1" key={index}>
                    <div
                      className="grid-item card custom-card p-0"
                      style={{
                        alignItems: "center",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Image
                        style={{ objectFit: "contain" }}
                        height={"100%"}
                        width={"100%"}
                        src={item.image_url}
                        fallback="https://via.placeholder.com/150"
                      />
                    </div>
                  </div>
                ))}
              </Image.PreviewGroup>
            ) : (
              <div className="w-100 d-flex justify-content-center p-4">
                <Empty />
              </div>
            )}
          </div>
        </Spin>
      )}
      {data.length > 0 && (
        <Pagination
          current={page}
          total={totalPictures}
          pageSize={20}
          align="end"
          showSizeChanger={false}
          style={{ marginTop: 16 }}
          onChange={(newPage) => {
            setPage(newPage);
            scrollToTop();
          }}
        />
      )}
      <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
        <Image.PreviewGroup
          preview={{
            visible: previewVisible,
            current: previewIndex,
            onVisibleChange: (vis) => setPreviewVisible(vis),
            onChange: (current) => setPreviewIndex(current),
          }}
        >
          {data.map((item) => (
            <Image key={item.id} src={item.image_url} />
          ))}
        </Image.PreviewGroup>
      </div>
      <Modal
        open={issuesList.show}
        title="Issues"
        onCancel={() => {
          setIssuesList({
            show: false,
            data: [],
          });
        }}
        hideFooter
      >
        <Table
          columns={issueColumns}
          data={issuesList.data}
          loading={issuesList.loading}
        />
      </Modal>
    </>
  );
}
